/** @format */
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { PlanValidationResult, ValidatablePlan } from 'common-typescript';
import {
    AnyCourseUnitRule,
    CourseUnit,
    CourseUnitResultItem,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    EntityWithRule,
    OtmId,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../../plan/plan-actions-service/plan-actions.service';
import { ReadMoreModalService } from '../../../read-more-ng/read-more-modal.service';
import { ReadMoreValues } from '../../../read-more-ng/read-more-ng-modal/read-more-ng-modal.component';
import { PlanData, PlanStateObject } from '../../../service/plan-state.service';

@Component({
    selector: 'sis-plan-structure-any-course-unit-rule',
    templateUrl: './plan-structure-any-course-unit-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanStructureAnyCourseUnitRuleComponent implements OnChanges, OnDestroy {
    @Input({ required: true }) rule: AnyCourseUnitRule;
    @Input({ required: true }) parentModule: EntityWithRule;
    @Input({ required: true }) planValidationResult: PlanValidationResult;
    @Input({ required: true }) planStateObject: PlanStateObject;
    @Input({ required: true }) planData: PlanData;
    @Input({ required: true }) validatablePlan: ValidatablePlan;
    @Input({ required: true }) ruleDepthLevel: number;
    @Input({ required: true }) headingLevel: number;
    @Input({ required: true }) selectionUIState: 'ACTIVE' | 'DISABLED' | 'SELECTABLE' = 'ACTIVE';
    @Input({ required: true }) currentUniversityOrgId: OtmId;
    @Input({ required: true }) crossStudyFeaturesEnabled: boolean;
    @Input({ required: true }) cooperationNetworkIds: OtmId[];
    @Input({ required: true }) curriculumPeriodIds: OtmId[];

    courseUnits: CourseUnit[] = [];
    customCourseUnitAttainments: CustomCourseUnitAttainment[] = [];
    customStudyDrafts: CustomStudyDraft[] = [];
    courseUnitInPlanMap: { [id: string]: boolean } = {};
    customCourseUnitAttainmentInPlanMap: { [id: string]: boolean } = {};
    customStudyDraftInPlanMap: { [id: string]: boolean } = {};
    excludedCourseUnitIds = new Set<string>();

    private destroy$ = new Subject<void>();

    constructor(
        private translocoService: TranslocoService,
        private readMoreModalService: ReadMoreModalService,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
    ) {}

    ngOnChanges() {
        const ruleValidationResult = _.get(this.planValidationResult.ruleValidationResults, [this.parentModule.id, this.rule.localId]);
        if (ruleValidationResult) {
            const courseUnitsInPlan = _.filter(
                _.values(ruleValidationResult.selectedCourseUnitsById),
                cu => !this.validatablePlan.isCourseUnitInPlanAsSubstitute(cu),
            );

            this.courseUnits = this.concatAndClean<CourseUnit>(this.courseUnits, courseUnitsInPlan);
            _.forEach(this.courseUnits, cu => (this.courseUnitInPlanMap[cu.id] = this.validatablePlan.isCourseUnitInPlan(cu)));

            const customCourseUnitAttainmentsInPlan = _.values(ruleValidationResult.selectedCustomCourseUnitAttainmentsById);
            this.customCourseUnitAttainments = this.concatAndClean<CustomCourseUnitAttainment>(
                this.customCourseUnitAttainments,
                customCourseUnitAttainmentsInPlan,
            );
            _.forEach(
                this.customCourseUnitAttainments,
                ccua => (this.customCourseUnitAttainmentInPlanMap[ccua.id] = this.validatablePlan.isCustomCourseUnitAttainmentInPlan(ccua)),
            );

            const customStudyDraftsInPlan = _.values(ruleValidationResult.selectedCustomStudyDraftsById);
            this.customStudyDrafts = this.concatAndClean<CustomStudyDraft>(this.customStudyDrafts, customStudyDraftsInPlan);
            _.forEach(
                this.customStudyDrafts,
                csd =>
                    (this.customStudyDraftInPlanMap[csd.id] = !_.isNil(
                        _.find(this.validatablePlan.plan?.customStudyDrafts, customStudyDraft => customStudyDraft.id === csd.id),
                    )),
            );
            this.excludedCourseUnitIds = new Set<string>(_.map(this.courseUnits, 'id'));
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleCUSelectToggle(courseUnit: CourseUnit): void {
        const parentModule = this.parentModule;
        if (!this.courseUnitInPlanMap[courseUnit.id]) {
            this.planActionsService?.selectCourseUnit?.(courseUnit, parentModule);
        } else {
            this.planActionsService?.removeCourseUnit?.(courseUnit, parentModule);
        }
    }

    handleCCUASelectToggle(customCourseUnitAttainment: CustomCourseUnitAttainment): void {
        const parentModule = this.parentModule;
        if (!this.customCourseUnitAttainmentInPlanMap[customCourseUnitAttainment.id]) {
            this.planActionsService?.selectCustomCourseUnitAttainment?.(customCourseUnitAttainment, parentModule);
        } else {
            this.planActionsService?.removeCustomCourseUnitAttainmentById?.(customCourseUnitAttainment.id, parentModule);
        }
    }

    handleCSDSelectToggle(customStudyDraft: CustomStudyDraft): void {
        const parentModule = this.parentModule;
        if (!this.customStudyDraftInPlanMap[customStudyDraft.id]) {
            this.planActionsService?.addCustomStudyDraft?.(customStudyDraft, parentModule);
        } else {
            this.planActionsService?.removeCustomStudyDraft?.(customStudyDraft, parentModule);
        }
    }

    openAddStudyDraftModal() {
        this.planActionsService.openCustomStudyDraftCreationModal(this.parentModule);
    }

    openReadMoreAboutStudyDraftModal() {
        this.readMoreModalService.open(this.getReadMoreOptions());
    }

    getReadMoreOptions(): ReadMoreValues {
        return {
            options: {
                contentHtml: null,
                contentTranslateKey: 'PLAN_EDIT.SELECTION_MODAL.ANY_COURSE_UNIT_RULE.CUSTOM_STUDY_DRAFT_READ_MORE_DESCRIPTION',
                contentTranslateParameters: null,
                title: this.translocoService.translate('PLAN_EDIT.SELECTION_MODAL.ANY_COURSE_UNIT_RULE.CUSTOM_STUDY_DRAFT_READ_MORE_TITLE'),
            },
        };
    }

    updateSelected(courseUnit: CourseUnitResultItem): void {
        if (courseUnit) {
            this.planActionsService?.selectCourseUnitByGroupId(courseUnit.groupId, this.parentModule);
        }
    }

    concatAndClean<T>(visibleSelections: T[], newSelections: T[]): T[] {
        return _.flow([_.concat, _.compact, arr => _.uniqBy(arr, 'id')])(visibleSelections, newSelections);
    }
}
